import { useId, type ReactNode } from 'react';

import { Chip } from './Chip.js';
import './chip.css';

export type OpcaoDeChip<T extends string> = {
  valor: T;
  rotulo: string;
  icone?: ReactNode;
};

export type PropsGrupoDeChips<T extends string> = {
  /** O `name` compartilhado. É ele que faz o navegador tratar os rádios
   *  como um grupo — e daí vêm as setas e o VoiceOver, de graça. */
  nome: string;
  rotulo: string;
  /** Texto de apoio do grupo inteiro, ligado por `aria-describedby`
   *  (Ajustes › Escala de navegação já tem um). */
  ajuda?: string;
  opcoes: ReadonlyArray<OpcaoDeChip<T>>;
  valor: T | null;
  aoMudar: (valor: T) => void;
  className?: string;
};

export function GrupoDeChips<T extends string>({
  nome,
  rotulo,
  ajuda,
  opcoes,
  valor,
  aoMudar,
  className,
}: PropsGrupoDeChips<T>) {
  const gerado = useId();
  const idAjuda = `grupo${gerado}-ajuda`;
  return (
    <fieldset
      className={
        className ? `ds-grupo-de-chips ${className}` : 'ds-grupo-de-chips'
      }
      aria-describedby={ajuda ? idAjuda : undefined}
    >
      <legend className="ds-grupo-de-chips__legenda">{rotulo}</legend>
      {ajuda ? (
        <p className="ds-grupo-de-chips__ajuda" id={idAjuda}>
          {ajuda}
        </p>
      ) : null}
      <div className="ds-grupo-de-chips__lista">
        {opcoes.map((opcao) => (
          <Chip
            key={opcao.valor}
            name={nome}
            value={opcao.valor}
            rotulo={opcao.rotulo}
            icone={opcao.icone}
            selecionado={valor === opcao.valor}
            // Nada de `onKeyDown`: a seta já é do rádio nativo, e ela chega
            // aqui como um `change` como qualquer outro.
            onChange={() => {
              aoMudar(opcao.valor);
            }}
          />
        ))}
      </div>
    </fieldset>
  );
}
